import { Mic, MicOff, Square, X } from "lucide-react";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useVoiceRecording } from "@/domains/ai/ai-chat/hooks/use-voice-recording";
import { cn } from "@/lib/utils";

const WAVEFORM_BARS = Array.from({ length: 20 }, (_, i) => ({
  id: `waveform-bar-${i}`,
  delay: `${i * 0.05}s`,
}));

interface VoiceComposerProps {
  onTranscript: (text: string) => void;
  onCancel: () => void;
  className?: string;
}

/**
 * Voice recording interface that replaces the text input during recording
 *
 * Shows recording status, duration, waveform animation, and control buttons
 */
export const VoiceComposer: FC<VoiceComposerProps> = ({ onTranscript, onCancel, className }) => {
  const { t } = useTranslation("ai-chat");
  const {
    isRecording,
    isProcessing,
    error,
    duration,
    isSupported,
    hasPermission,
    startRecording,
    stopRecording,
    cancelRecording,
    clearError,
  } = useVoiceRecording({
    onTranscriptComplete: (transcript) => {
      // Pass transcript to parent for review
      onTranscript(transcript);
    },
    onError: (err) => {
      console.error("[VoiceComposer] Error:", err);
    },
  });

  // Auto-start recording when component mounts
  useEffect(() => {
    if (isSupported && hasPermission !== false) {
      startRecording().catch((err) => {
        console.error("[VoiceComposer] Failed to start recording:", err);
      });
    }
  }, [hasPermission, isSupported, startRecording]);

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle cancel
  const handleCancel = () => {
    cancelRecording();
    clearError();
    onCancel();
  };

  // Handle stop recording
  const handleStop = async () => {
    try {
      await stopRecording();
      // Note: onTranscript is called automatically via the hook callback
      // User can review and manually send the transcript
    } catch (err) {
      console.error("[VoiceComposer] Failed to stop recording:", err);
    }
  };

  // Show error state
  if (error) {
    return (
      <div
        className={cn(
          "flex w-full flex-col items-center justify-center rounded-3xl border border-destructive bg-destructive/10 px-6 py-8",
          className
        )}
      >
        <MicOff className="mb-4 h-12 w-12 text-destructive" />
        <p className="mb-2 text-sm font-medium text-destructive">{error.message}</p>
        <p className="mb-4 text-xs text-muted-foreground">
          {error.message.includes("permission")
            ? t("voice.microphoneAccessDenied")
            : t("voice.error")}
        </p>
        <Button onClick={handleCancel} variant="outline" size="sm">
          {t("sidebar.close")}
        </Button>
      </div>
    );
  }

  // Show processing state
  if (isProcessing) {
    return (
      <div
        className={cn(
          "flex w-full flex-col items-center justify-center rounded-3xl border border-border bg-muted px-6 py-8",
          className
        )}
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
          <div
            className="h-2 w-2 animate-pulse rounded-full bg-primary"
            style={{ animationDelay: "0.2s" }}
          />
          <div
            className="h-2 w-2 animate-pulse rounded-full bg-primary"
            style={{ animationDelay: "0.4s" }}
          />
        </div>
        <p className="text-sm font-medium text-foreground">{t("voice.transcribing")}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t("voice.transcribingSubtitle")}</p>
      </div>
    );
  }

  // Show recording state
  return (
    <div
      className={cn(
        "flex w-full flex-col items-center rounded-3xl border border-border bg-muted px-6 py-6",
        className
      )}
    >
      {/* Recording indicator and duration */}
      <div className="mb-6 flex flex-col items-center gap-3">
        {/* Microphone icon with pulse animation */}
        <div className="relative">
          <div className="absolute inset-0 animate-ping rounded-full bg-red-500/50" />
          <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-red-500">
            <Mic className="h-8 w-8 text-white" />
          </div>
        </div>

        {/* Duration */}
        <div className="text-2xl font-mono font-semibold tabular-nums text-foreground">
          {formatDuration(duration)}
        </div>

        {/* Status text */}
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          <p className="text-sm font-medium text-muted-foreground">{t("voice.recording")}</p>
        </div>
      </div>

      {/* Waveform visualization */}
      <div className="mb-6 flex h-12 items-center justify-center gap-1">
        {WAVEFORM_BARS.map(({ id, delay }) => (
          <div
            key={id}
            className="w-1 rounded-full bg-primary/70 transition-all"
            style={{
              height: isRecording ? `${20 + Math.random() * 60}%` : "20%",
              animationDelay: delay,
            }}
          />
        ))}
      </div>

      {/* Control buttons */}
      <div className="flex items-center gap-3">
        <Button onClick={handleCancel} variant="outline" size="lg" className="gap-2">
          <X className="h-4 w-4" />
          {t("voice.cancel")}
        </Button>

        <Button
          onClick={handleStop}
          variant="default"
          size="lg"
          className="gap-2"
          disabled={duration < 1}
        >
          <Square className="h-4 w-4 fill-current" />
          {t("voice.done")}
        </Button>
      </div>

      {/* Helper text */}
      <p className="mt-4 text-center text-xs text-muted-foreground">
        {t("voice.recordingSubtitle")}
      </p>
    </div>
  );
};
